/**
 * civ_row.ts — CLIENT row-first civilization resolution (conquest, war rework).
 *
 * A conquered settlement's `settlement_civ` ROW flips; the seed derivation is
 * only the fallback for towns the sim has not seeded. Mirrors the server's
 * civ_ops.civOfSettlementRow — keep both row-first or a conquest changes the
 * map but not the people.
 */
import { civilizationOf, type Civilization } from './biome_settlements.js';
import { atlas } from '../state.js';

interface CivRowTbl { settlementId: { find(id: bigint): { civ: string } | null | undefined } }

/** Row-first civ for a settlement id (site.id + 1), seed as the fallback. */
export function civOfSettlementRowC(settlementId: bigint | number, siteSeed: number): Civilization {
  try {
    const tbl = (atlas.conn?.db as unknown as { settlementCiv?: CivRowTbl } | undefined)?.settlementCiv;
    const row = tbl?.settlementId.find(BigInt(settlementId));
    if (row && (row.civ === 'a' || row.civ === 'b')) return row.civ;
  } catch { /* pre-publish server */ }
  return civilizationOf(siteSeed);
}

let gen = 0;
let hooked = false;

/** Bumps whenever any settlement_civ row changes — include it in any cache key
 *  derived from civ (map names, body colors) so a conquest invalidates it. */
export function civRowGeneration(): number {
  if (!hooked) {
    try {
      const tbl = (atlas.conn?.db as unknown as { settlementCiv?: { onUpdate(f: () => void): void; onInsert(f: () => void): void } } | undefined)?.settlementCiv;
      if (tbl) { tbl.onUpdate(() => { gen++; }); tbl.onInsert(() => { gen++; }); hooked = true; }
    } catch { /* pre-publish server */ }
  }
  return gen;
}

/**
 * CIVIC WAVE — the unaligned towns the lore promised ("they trade with both,
 * marry into both, and vote in neither"): a pure ~5% of settlements, hosts
 * for peace congresses. BYTE-MIRROR of server lib/civ_ops.isNeutralTown
 * (FNV-1a h01 over `neutral` — keep in lockstep).
 */
export function isNeutralTownC(settlementId: bigint): boolean {
  let h = 0x811c9dc5;
  const s = `${settlementId}|neutral`;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0) / 0xffffffff < 0.055;
}
